import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Link } from 'react-router-dom';
import { Wrench, ArrowLeft, Plus, Search, Edit, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import Header from './Header';
import TechnicianForm from './TechnicianForm';
import SearchCounter from './SearchCounter';
import { useAuth } from '../contexts/AuthContext';
import { 
  getAllTechnicians, 
  getTechniciansInUse, 
  isTechnicianInUse,
  searchTechniciansLocal 
} from '../lib/technicianService';
import { matchesSearchTerm } from '../lib/searchUtils';

const TechniciansList = () => {
  const { currentUser, userRole } = useAuth();
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [success, setSuccess] = useState('');
  const [editingTechnician, setEditingTechnician] = useState(null);
  const [techniciansInUse, setTechniciansInUse] = useState(new Set());

  useEffect(() => {
    fetchTechnicians();
  }, [showArchived]);

  // Removed aggressive window focus refresh to prevent reloading on tab switch

  const fetchTechnicians = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError('');
      
      // Use local-first technician service
      const techniciansList = await getAllTechnicians(showArchived, forceRefresh);
      setTechnicians(techniciansList);
      
      // Check which technicians are used in orders (optimized)
      await checkTechniciansUsage(techniciansList);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      console.log('Technicians collection may not exist yet, starting with empty list');
      setTechnicians([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const checkTechniciansUsage = async (techniciansList) => {
    try {
      // Use optimized batch usage check instead of individual queries
      const technicianIds = techniciansList.map(tech => tech.id);
      const inUseSet = await getTechniciansInUse(technicianIds);
      setTechniciansInUse(inUseSet);
    } catch (error) {
      console.error('Error checking technicians usage:', error);
    }
  };

  const filteredTechnicians = technicians.filter(tech => {
    // If no search term, include all technicians
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }
    
    return (
      matchesSearchTerm(tech.nombre, searchTerm) ||
      matchesSearchTerm(tech.telefono, searchTerm) ||
      matchesSearchTerm(tech.domicilio, searchTerm)
    );
  });

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    const months = [
      'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
      'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'
    ];
    
    const day = dateObj.getUTCDate().toString().padStart(2, '0');
    const month = months[dateObj.getUTCMonth()];
    const year = dateObj.getUTCFullYear();
    
    return `${day}/${month}/${year}`;
  };

  const calculateAntiguedad = (fechaIngreso, fechaBaja) => {
    if (!fechaIngreso) return '';
    
    const startDate = fechaIngreso.toDate ? fechaIngreso.toDate() : new Date(fechaIngreso);
    const endDate = fechaBaja ? 
      (fechaBaja.toDate ? fechaBaja.toDate() : new Date(fechaBaja)) : 
      new Date();
    
    const diffTime = endDate - startDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0 && months > 0) {
      return `(Antigüedad: ${years} año${years > 1 ? 's' : ''}, ${months} mes${months > 1 ? 'es' : ''})`;
    } else if (years > 0) {
      return `(Antigüedad: ${years} año${years > 1 ? 's' : ''})`;
    } else if (months > 0) {
      return `(Antigüedad: ${months} mes${months > 1 ? 'es' : ''})`;
    } else {
      const days = Math.max(0, diffDays);
      return `(Antigüedad: ${days} día${days !== 1 ? 's' : ''})`;
    }
  };

  const calculateAge = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';
    
    const birthDate = fechaNacimiento.toDate ? fechaNacimiento.toDate() : new Date(fechaNacimiento);
    const today = new Date();
    
    const diffTime = today - birthDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0 && months > 0) {
      return `(Edad: ${years} año${years > 1 ? 's' : ''}, ${months} mes${months > 1 ? 'es' : ''})`;
    } else if (years > 0) {
      return `(Edad: ${years} año${years > 1 ? 's' : ''})`;
    } else if (months > 0) {
      return `(Edad: ${months} mes${months > 1 ? 'es' : ''})`;
    } else {
      const days = Math.max(0, diffDays);
      return `(Edad: ${days} día${days !== 1 ? 's' : ''})`;
    }
  };

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'activo':
        return 'Activo';
      case 'inactivo':
        return 'Inactivo';
      case 'suspendido':
        return 'Suspendido';
      default:
        return status || 'N/A';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'activo':
        return 'text-green-600';
      case 'inactivo':
        return 'text-gray-600';
      case 'suspendido':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const handleFormSuccess = () => {
    const message = editingTechnician ? 'Técnico actualizado exitosamente' : 'Técnico creado exitosamente';
    setSuccess(message);
    setEditingTechnician(null);
    fetchTechnicians(true); // Force refresh to update cache
    setTimeout(() => setSuccess(''), 5000); // Clear success message after 5 seconds
  };

  const handleEdit = (technician) => {
    setEditingTechnician(technician);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTechnician(null);
  };

  const handleArchive = async (technician) => {
    if (!window.confirm(`¿Está seguro de archivar a ${technician.nombre}?`)) return;

    try {
      await updateDoc(doc(db, 'technicians', technician.id), {
        isArchived: true,
        updatedAt: new Date()
      });
      setSuccess(`${technician.nombre} ha sido archivado`);
      fetchTechnicians(true); // Force refresh to update cache
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error archiving technician:', error);
      setError('Error al archivar el técnico');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnarchive = async (technician) => {
    if (!window.confirm(`¿Está seguro de desarchivar a ${technician.nombre}?`)) return;

    try {
      await updateDoc(doc(db, 'technicians', technician.id), {
        isArchived: false,
        updatedAt: new Date()
      });
      setSuccess(`${technician.nombre} ha sido desarchivado`);
      fetchTechnicians(true); // Force refresh to update cache
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error unarchiving technician:', error);
      setError('Error al desarchivar el técnico');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (technician) => {
    try {
      // Check if technician is assigned to any orders (optimized)
      const isInUse = await isTechnicianInUse(technician.id);
      
      if (isInUse) {
        setError(`No se puede eliminar a ${technician.nombre} porque está asignado a una o más órdenes de trabajo. Debe archivar el técnico en su lugar.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente a ${technician.nombre}?\n\n` +
        `Esta acción no se puede deshacer. Si desea mantener el historial, ` +
        `considere archivar el técnico en su lugar.`
      )) {
        return;
      }

      // Delete the technician
      await deleteDoc(doc(db, 'technicians', technician.id));
      setSuccess(`${technician.nombre} ha sido eliminado permanentemente`);
      fetchTechnicians(true); // Force refresh to update cache
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting technician:', error);
      setError('Error al eliminar el técnico');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al Dashboard</span>
          </Link>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Wrench className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Técnicos</h2>
              </div>
              {canEdit() && (
                <button 
                  onClick={() => setShowForm(true)}
                  className="flex items-center justify-center w-10 h-10 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400"
                  title="Nuevo Técnico"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar and Filters */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md mb-4">
                {success}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Buscar técnicos por nombre, teléfono o domicilio..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
                <SearchCounter 
                  count={filteredTechnicians.length} 
                  label="técnicos" 
                  isSearching={!!searchTerm} 
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showArchived"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-4 w-4 text-orange-600 dark:text-orange-400 focus:ring-orange-500 dark:focus:ring-orange-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
                <label htmlFor="showArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Mostrar archivados
                </label>
              </div>
            </div>
          </div>

          {/* Technicians List */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Cargando técnicos...
              </div>
            ) : filteredTechnicians.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchTerm ? 'No se encontraron técnicos que coincidan con la búsqueda' : 'No hay técnicos registrados'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                        Teléfono
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                        Domicilio
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">
                        <div className="whitespace-normal leading-tight">
                          Fecha de Nacimiento
                        </div>
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                        <div className="whitespace-normal leading-tight">
                          Fecha de Ingreso
                        </div>
                      </th>
                      {canEdit() && (
                        <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredTechnicians.map((technician) => (
                      <tr key={technician.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => window.location.href = `/technicians/${technician.id}`}>
                        <td className="px-2 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          <div className="min-w-0">
                            <div className="truncate" title={technician.nombre}>
                              {technician.nombre || 'N/A'}
                            </div>
                            {technician.isArchived && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 mt-1">
                                Archivado
                              </span>
                            )}
                            {/* Show additional info on mobile */}
                            <div className="sm:hidden mt-2 space-y-1">
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium">Tel:</span> {technician.telefono || 'N/A'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium">Ingreso:</span> {formatDate(technician.fechaIngreso)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {calculateAntiguedad(technician.fechaIngreso, technician.fechaBaja)}
                              </div>
                              {technician.fechaNacimiento && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  <span className="font-medium">Nacimiento:</span> {formatDate(technician.fechaNacimiento)} {calculateAge(technician.fechaNacimiento)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 hidden sm:table-cell">
                          {technician.telefono || 'N/A'}
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 dark:text-gray-300 hidden lg:table-cell">
                          <div className="max-w-xs">
                            <div className="text-sm leading-tight break-words" title={technician.domicilio}>
                              {technician.domicilio || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 dark:text-gray-300 hidden xl:table-cell">
                          <div className="text-xs">
                            <div>{formatDate(technician.fechaNacimiento)}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {calculateAge(technician.fechaNacimiento)}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-sm text-gray-900 dark:text-gray-300 hidden md:table-cell">
                          <div className="text-xs">
                            <div>{formatDate(technician.fechaIngreso)}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {calculateAntiguedad(technician.fechaIngreso, technician.fechaBaja)}
                            </div>
                          </div>
                        </td>
                        {canEdit() && (
                          <td className="px-1 py-4 text-sm font-medium">
                            <div className="flex flex-col sm:flex-row sm:space-x-1 space-y-1 sm:space-y-0" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => handleEdit(technician)}
                                className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                title="Editar técnico"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              {technician.isArchived ? (
                                <button
                                  onClick={() => handleUnarchive(technician)}
                                  className="p-1 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                  title="Desarchivar técnico"
                                >
                                  <ArchiveRestore className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleArchive(technician)}
                                  className="p-1 text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                                  title="Archivar técnico"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              )}
                              {canDelete() && (
                                <button
                                  onClick={() => !techniciansInUse.has(technician.id) && handleDelete(technician)}
                                  className={`p-1 rounded ${
                                    techniciansInUse.has(technician.id)
                                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                      : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                                  }`}
                                  title={
                                    techniciansInUse.has(technician.id)
                                      ? 'No se puede eliminar: técnico asignado a órdenes de trabajo'
                                      : 'Eliminar técnico'
                                  }
                                  disabled={techniciansInUse.has(technician.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <TechnicianForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingTechnician={editingTechnician}
      />
    </div>
  );
};

export default TechniciansList;